import {
  type CreateOrderBody,
  NF_STATUS_LABELS,
  NfStatus,
  Order,
  OrderPreview,
  type OrderItemInput,
  POSTED_METHODS,
  PricingCalculateRequest,
  Quote,
  QuoteList,
  SHIPPING_METHOD_LABELS,
  ShippingMethod,
  addDaysIso,
  formatBRL,
  formatIsoDate,
  isoDateInSaoPaulo,
} from '@deizi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { AffixInput, Field } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Combobox } from '../../components/Combobox';
import { channelOption, kitOption, variantOption } from '../../components/pickers';
import { Modal } from '../../components/Overlays';
import { PageHead } from '../../components/PageHead';
import { Alert } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { useChannels, useKits } from '../../lib/catalog-api';
import { errorMessage } from '../../lib/messages';
import { centavosInput, parseBrDate, parseCentavos } from '../../lib/numbers';
import { useInvalidateOrders } from '../../lib/orders-api';
import { useCatalog } from '../../lib/products-api';
import { DuplicateWarning } from '../customers/CustomerSearch';
import { useCustomers } from '../../lib/quotes-api';
import { useDebounced } from '../../lib/use-debounced';
import { useIsPhone } from '../../lib/use-media';

type Request = z.input<typeof PricingCalculateRequest>;

interface Line {
  key: string;
  kind: 'variant' | 'quote' | 'request';
  variantId: string;
  quoteItemId?: string;
  request?: Request;
  description?: string;
  slicedFileId?: string | null;
  thumbnailFileId?: string | null;
  qty: string;
  price: string;
}

/** What Quick Quote hands over ("Converter em pedido") in the navigation state. */
export const FromQuickQuote = z.object({
  request: PricingCalculateRequest,
  description: z.string().min(1),
  quantity: z.number().int().min(1),
  unitPriceCentavos: z.number().int().nullable(),
  channelId: z.string().nullable(),
  slicedFileId: z.string().nullable(),
  thumbnailFileId: z.string().nullable(),
});
export type FromQuickQuote = z.input<typeof FromQuickQuote>;
const NavState = z.object({ fromQuickQuote: FromQuickQuote });

const CHARGES = [
  ['commissionCentavos', 'Comissão'],
  ['fixedFeeCentavos', 'Tarifa fixa'],
  ['shippingCentavos', 'Frete pago por você'],
  ['discountsCentavos', 'Cupons e descontos'],
] as const;
type ChargeKey = (typeof CHARGES)[number][0];

function QuotePicker({ onPick, onClose }: { onPick: (q: Quote) => void; onClose: () => void }) {
  const list = useQuery({
    queryKey: ['quotes', 'accepted'],
    queryFn: () => api.get('/quotes?status=accepted&limit=50', QuoteList),
  });
  const [loading, setLoading] = useState<string | null>(null);
  return (
    <Modal title="Puxar de orçamento aceito" wide onClose={onClose}>
      <p className="sm muted">
        Itens, preços e custos congelados vêm do orçamento. O orçamento vira Convertido.
      </p>
      {(list.data?.data ?? []).map((q) => (
        <div
          key={q.id}
          className="row between card sunk"
          style={{ gap: 10, padding: 12, borderRadius: 12 }}
        >
          <span className="sm">
            <b>{q.number}</b> · {q.customer?.name ?? 'sem cliente'} — {q.itemsSummary} ·{' '}
            {q.channel?.name ?? '—'}
          </span>
          <span className="row" style={{ gap: 10 }}>
            <b className="tab">{formatBRL(q.totals.priceCentavos)}</b>
            <button
              type="button"
              className="btn sm"
              disabled={loading !== null}
              onClick={() => {
                setLoading(q.id);
                void api
                  .get(`/quotes/${q.id}`, Quote)
                  .then(onPick)
                  .finally(() => setLoading(null));
              }}
            >
              Usar
            </button>
          </span>
        </div>
      ))}
      {list.isSuccess && list.data.data.length === 0 ? (
        <p className="sm muted">
          Nenhum orçamento aceito. <Link to="/orcamentos">Ver todos os orçamentos</Link>
        </p>
      ) : null}
      <p className="xs muted">Só aparecem orçamentos com situação Aceito.</p>
    </Modal>
  );
}

/** "P-… criado" (board Pedido-Novo): what creating the order did, and where to go next. */
function OrderCreated({ o, onAnother }: { o: Order; onAnother: () => void }) {
  const fromStock = o.items.reduce((n, i) => n + i.fromStockUnits, 0);
  const fees =
    o.charges.commissionCentavos +
    o.charges.fixedFeeCentavos +
    o.charges.shippingCentavos +
    o.charges.taxCentavos;
  const rows: [string, string][] = [
    ...(fromStock
      ? ([
          [
            `${fromStock} ${fromStock === 1 ? 'unidade saiu' : 'unidades saíram'} da pronta entrega`,
            'estoque atualizado',
          ],
        ] as [string, string][])
      : []),
    ...(o.jobs.length
      ? ([
          [
            `${o.jobs.length} ${o.jobs.length === 1 ? 'impressão entrou' : 'impressões entraram'} na fila`,
            'Fila de impressão',
          ],
        ] as [string, string][])
      : []),
    ['Tarifas registradas', formatBRL(fees)],
    [
      'Custo congelado',
      `${formatBRL(o.profit.estimatedCostCentavos)} · ${formatIsoDate(o.soldOn)}`,
    ],
    [
      'Envio',
      `${o.shippingMethod ? SHIPPING_METHOD_LABELS[o.shippingMethod] : 'a definir'}${o.shipBy ? ` · até ${formatIsoDate(o.shipBy)}` : ''}`,
    ],
    ['Nota fiscal', NF_STATUS_LABELS[o.nfStatus]],
  ];
  return (
    <div className="col" style={{ gap: 18, maxWidth: 640 }}>
      <section className="card col" style={{ gap: 12 }}>
        <div className="row" style={{ gap: 12 }}>
          <span className="badge ok" style={{ width: 40, height: 40, justifyContent: 'center' }}>
            <Icon name="check" />
          </span>
          <div>
            <h1 style={{ fontSize: 24 }}>{o.number} criado</h1>
            <p className="sm muted">
              {o.channel.name} · {o.customer?.name ?? 'sem cliente'} ·{' '}
              {formatBRL(o.profit.totalCentavos)} · lucro estimado{' '}
              {formatBRL(o.profit.estimatedProfitCentavos)}
            </p>
          </div>
        </div>
        <div>
          {rows.map(([k, v]) => (
            <div key={k} className="kv sm">
              <span>{k}</span>
              <span className="v">{v}</span>
            </div>
          ))}
        </div>
        <div className="row wrap" style={{ gap: 10 }}>
          <Link className="btn p" to={`/pedidos/${o.id}`}>
            Abrir {o.number}
          </Link>
          {o.jobs.length ? (
            <Link className="btn" to="/fila">
              Ver fila de impressão
            </Link>
          ) : null}
          <button type="button" className="btn g" onClick={onAnother}>
            Registrar outro pedido
          </button>
        </div>
      </section>
    </div>
  );
}

/** Novo pedido (board Pedido-Novo; the phone uses the same form stacked). */
export function NewOrderPage() {
  const [created, setCreated] = useState<Order | null>(null);
  const [round, setRound] = useState(0);
  const navigate = useNavigate();
  if (created) {
    return (
      <OrderCreated
        o={created}
        onAnother={() => {
          setCreated(null);
          setRound((n) => n + 1);
          void navigate('/pedidos/novo', { replace: true, state: null });
        }}
      />
    );
  }
  return <NewOrderForm key={round} onCreated={setCreated} />;
}

function NewOrderForm({ onCreated }: { onCreated: (o: Order) => void }) {
  const phone = useIsPhone();
  const toast = useToast();
  const invalidate = useInvalidateOrders();
  const location = useLocation();
  const parsedState = NavState.safeParse(location.state);
  const fromQq = parsedState.success ? parsedState.data.fromQuickQuote : undefined;
  const channels = useChannels();
  const kits = useKits();
  const active = (channels.data?.data ?? []).filter((c) => c.active && c.current);
  const [idem] = useState(newIdempotencyKey);
  const [channelId, setChannelId] = useState(fromQq?.channelId ?? '');
  const channel = active.find((c) => c.id === channelId) ?? active[0];
  const [mk, setMk] = useState('');
  const [soldOn, setSoldOn] = useState(formatIsoDate(isoDateInSaoPaulo()));
  const [customerQ, setCustomerQ] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [newName, setNewName] = useState<string | null>(null);
  const [newWhats, setNewWhats] = useState('');
  const customers = useCustomers(customerQ);
  const [lines, setLines] = useState<Line[]>(() =>
    fromQq
      ? [
          {
            key: crypto.randomUUID(),
            kind: 'request',
            variantId: '',
            request: fromQq.request,
            description: fromQq.description,
            slicedFileId: fromQq.slicedFileId,
            thumbnailFileId: fromQq.thumbnailFileId,
            qty: String(fromQq.quantity),
            price: fromQq.unitPriceCentavos ? centavosInput(fromQq.unitPriceCentavos) : '',
          },
        ]
      : [],
  );
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [charges, setCharges] = useState<Partial<Record<ChargeKey, string>>>({});
  const [method, setMethod] = useState<ShippingMethod | ''>('');
  const [shipBy, setShipBy] = useState(formatIsoDate(addDaysIso(isoDateInSaoPaulo(), 2)));
  const [tracking, setTracking] = useState('');
  const [nf, setNf] = useState<z.infer<typeof NfStatus>>('not_needed');
  const [kitId, setKitId] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const catalog = useCatalog(channel?.id ?? null, '');
  const options = (catalog.data?.data ?? []).filter((c) => !c.missing);

  const soldIso = parseBrDate(soldOn);
  const shipIso = shipBy.trim() ? parseBrDate(shipBy) : null;
  const items: OrderItemInput[] = lines.flatMap((l): OrderItemInput[] => {
    const qty = /^\d+$/.test(l.qty.trim()) ? Number(l.qty) : null;
    if (!qty) return [];
    const price = l.price.trim() ? parseCentavos(l.price) : null;
    const base = { quantity: qty, ...(price !== null ? { unitPriceCentavos: price } : {}) };
    if (l.kind === 'quote') return [{ quoteItemId: l.quoteItemId!, ...base }];
    if (l.kind === 'request') {
      return [
        {
          request: { ...l.request!, quantity: qty },
          description: l.description!,
          slicedFileId: l.slicedFileId ?? null,
          thumbnailFileId: l.thumbnailFileId ?? null,
          ...base,
        },
      ];
    }
    return l.variantId ? [{ variantId: l.variantId, ...base }] : [];
  });
  const editedCharges = Object.fromEntries(
    Object.entries(charges).flatMap(([k, v]) => {
      const c = parseCentavos(v ?? '');
      return c !== null ? [[k, c]] : [];
    }),
  );
  const body: CreateOrderBody | null =
    channel && items.length && soldIso
      ? {
          channelId: channel.id,
          paymentMethod: null,
          marketplaceOrderNo: mk.trim() || null,
          soldOn: soldIso,
          customerId: newName === null ? customerId : null,
          ...(newName !== null && newName.trim()
            ? { newCustomer: { name: newName.trim(), whatsapp: newWhats.trim() || null } }
            : {}),
          shipBy: shipIso,
          shippingMethod: method || null,
          trackingCode: tracking.trim() || null,
          nfStatus: nf,
          ...(kitId !== null ? { packagingKitId: kitId || null } : {}),
          charges: editedCharges,
          quoteId,
          items,
          confirm: true,
        }
      : null;
  const json = body ? JSON.stringify(body) : null;
  const debounced = useDebounced(json, 400);
  const preview = useQuery({
    queryKey: ['orders', 'preview', debounced],
    queryFn: () => api.post('/orders/preview', OrderPreview, JSON.parse(debounced!)),
    enabled: debounced !== null,
    placeholderData: (prev) => prev,
    retry: false,
  });
  const p = json ? preview.data : undefined;
  const create = useMutation({
    mutationFn: () => api.post('/orders', Order, body, { idempotencyKey: idem }),
    onSuccess: (o) => {
      invalidate();
      toast(`${o.number} criado`);
      onCreated(o);
      window.scrollTo({ top: 0 });
    },
  });
  const marketplace = (channel?.current?.commissionBps ?? 0) > 0;
  const posted = method !== '' && POSTED_METHODS.includes(method);
  const valid = !!body && !!soldIso && (shipBy.trim() === '' || shipIso !== null);

  const addVariant = () =>
    setLines([
      ...lines,
      {
        key: crypto.randomUUID(),
        kind: 'variant',
        variantId: options[0]?.variant.id ?? '',
        qty: '1',
        price: '',
      },
    ]);

  return (
    <>
      <PageHead crumb="Pedidos" title="Novo pedido" />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        <div className="col" style={{ flex: '3 1 560px', minWidth: 0, gap: 18 }}>
          <section className="card col" style={{ gap: 12 }}>
            <h2>Venda</h2>
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
              }}
            >
              <Field id="no-ch" label="Canal">
                <Combobox
                  id="no-ch"
                  value={channel?.id ?? ''}
                  options={active.map(channelOption)}
                  onChange={(v) => {
                    setChannelId(v);
                    setCharges({});
                    const name = active.find((c) => c.id === v)?.name.toLowerCase() ?? '';
                    if (name.includes('mercado') || name.startsWith('ml'))
                      setMethod('mercado_envios');
                    else if (name.includes('shopee')) setMethod('correios');
                  }}
                />
              </Field>
              <Field
                id="no-mk"
                label={`Nº do pedido no marketplace${marketplace ? ' · recomendado' : ' · opcional'}`}
                help={marketplace ? 'Copie do detalhe da venda.' : undefined}
              >
                <input
                  id="no-mk"
                  className="input"
                  placeholder="2000009…"
                  value={mk}
                  onChange={(e) => setMk(e.target.value)}
                />
              </Field>
              <Field
                id="no-date"
                label="Data da venda"
                error={tried && !soldIso ? 'Use DD/MM/AAAA.' : null}
              >
                <input
                  id="no-date"
                  className="input"
                  inputMode="numeric"
                  value={soldOn}
                  onChange={(e) => setSoldOn(e.target.value)}
                />
              </Field>
            </div>
            {newName === null ? (
              <Field id="no-cust" label="Cliente">
                <div className="row" style={{ gap: 8 }}>
                  <input
                    id="no-cust"
                    className="input grow"
                    list="no-cust-list"
                    placeholder="nome ou apelido"
                    value={customerQ}
                    onChange={(e) => {
                      setCustomerQ(e.target.value);
                      const hit = customers.data?.data.find((c) => c.name === e.target.value);
                      setCustomerId(hit?.id ?? null);
                    }}
                  />
                  <datalist id="no-cust-list">
                    {(customers.data?.data ?? []).map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </datalist>
                  <button type="button" className="btn sm" onClick={() => setNewName(customerQ)}>
                    + novo
                  </button>
                </div>
              </Field>
            ) : (
              <div className="card sunk col" style={{ gap: 10, padding: 12, borderRadius: 12 }}>
                <span className="lbl">Novo cliente</span>
                <div
                  style={{
                    display: 'grid',
                    gap: 12,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  }}
                >
                  <Field
                    id="no-nn"
                    label="Nome ou apelido"
                    error={tried && !newName.trim() ? 'Informe o nome.' : null}
                  >
                    <input
                      id="no-nn"
                      className="input"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                    />
                  </Field>
                  <Field id="no-nw" label="WhatsApp">
                    <input
                      id="no-nw"
                      className="input"
                      placeholder="opcional para marketplace"
                      value={newWhats}
                      onChange={(e) => setNewWhats(e.target.value)}
                    />
                  </Field>
                  <DuplicateWarning
                    name={newName}
                    whatsapp={newWhats}
                    onUse={(c) => {
                      setCustomerId(c.id);
                      setCustomerQ(c.name);
                      setNewName(null);
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn sm g"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => setNewName(null)}
                >
                  Cancelar
                </button>
              </div>
            )}
          </section>

          <section className="card flat">
            <div className="row between" style={{ padding: '16px 18px 6px' }}>
              <h2>Itens</h2>
              <div className="row" style={{ gap: 8 }}>
                <button type="button" className="btn sm" onClick={addVariant}>
                  + Item
                </button>
                <button type="button" className="btn sm" onClick={() => setPicking(true)}>
                  + Item de orçamento
                </button>
              </div>
            </div>
            <div className="gt" style={{ overflowX: 'auto' }}>
              {lines.map((l, i) => {
                const pi = p?.items[items.findIndex((_, k) => k === i)];
                const set = (patch: Partial<Line>) =>
                  setLines(lines.map((x) => (x.key === l.key ? { ...x, ...patch } : x)));
                return (
                  <div
                    key={l.key}
                    className="gtr"
                    style={
                      phone
                        ? { gridTemplateColumns: '72px 1fr 36px', rowGap: 6 }
                        : { gridTemplateColumns: 'minmax(150px, 1fr) 72px 130px 36px', rowGap: 6 }
                    }
                  >
                    {l.kind === 'variant' ? (
                      <Combobox
                        style={phone ? { gridColumn: '1 / -1' } : undefined}
                        ariaLabel="Produto e variação"
                        value={l.variantId}
                        options={options.map(variantOption)}
                        onChange={(v) => set({ variantId: v, price: '' })}
                      />
                    ) : (
                      <span className="sm" style={phone ? { gridColumn: '1 / -1' } : undefined}>
                        {l.description}
                        <span className="xs muted" style={{ display: 'block' }}>
                          {l.kind === 'quote' ? 'do orçamento' : 'do orçamento rápido'}
                        </span>
                      </span>
                    )}
                    <input
                      className="input"
                      aria-label="Quantidade"
                      inputMode="numeric"
                      style={{ textAlign: 'right' }}
                      value={l.qty}
                      onChange={(e) => set({ qty: e.target.value })}
                    />
                    <AffixInput
                      ariaLabel="Preço por unidade"
                      prefix="R$"
                      placeholder={pi ? centavosInput(pi.unitPriceCentavos) : ''}
                      value={l.price}
                      changed={l.price.trim() !== ''}
                      onChange={(v) => set({ price: v })}
                    />
                    <span style={{ gridColumn: '1 / -1', gridRow: phone ? 3 : 2 }}>
                      {pi ? (
                        pi.toPrint === 0 ? (
                          <span className="badge ok">
                            Pronta entrega · {pi.available} → {pi.available - pi.fromStock}
                          </span>
                        ) : (
                          <span className="badge warn">
                            {pi.fromStock ? `${pi.fromStock} da pronta entrega · ` : ''}Faltam{' '}
                            {pi.toPrint} · vai para a fila
                          </span>
                        )
                      ) : null}
                    </span>
                    <button
                      type="button"
                      className="btn sm g icon"
                      aria-label="Remover item"
                      onClick={() => setLines(lines.filter((x) => x.key !== l.key))}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              {lines.length === 0 ? (
                <div className="gtr" style={{ gridTemplateColumns: '1fr' }}>
                  <span className="sm" style={{ color: tried ? 'var(--err)' : 'var(--ink2)' }}>
                    Nenhum item. Adicione um produto ou puxe de um orçamento aceito.
                  </span>
                </div>
              ) : null}
            </div>
          </section>

          <section className="card col" style={{ gap: 12 }}>
            <div className="row between">
              <h2>Cobranças reais</h2>
              <span className="xs muted">
                {marketplace
                  ? 'copie do detalhe da venda no marketplace'
                  : 'sem comissão: deixe zero, a não ser que tenha dado desconto'}
              </span>
            </div>
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
              }}
            >
              {CHARGES.map(([k, label]) => {
                const predicted = p?.predicted[k];
                return (
                  <Field
                    key={k}
                    id={`no-${k}`}
                    label={label}
                    help={predicted !== undefined ? `previsto ${formatBRL(predicted)}` : undefined}
                  >
                    <AffixInput
                      id={`no-${k}`}
                      prefix="R$"
                      placeholder={predicted !== undefined ? centavosInput(predicted) : '0,00'}
                      value={charges[k] ?? ''}
                      changed={(charges[k] ?? '').trim() !== ''}
                      onChange={(v) => setCharges({ ...charges, [k]: v })}
                    />
                  </Field>
                );
              })}
            </div>
          </section>

          <section className="card col" style={{ gap: 12 }}>
            <h2>Envio e nota</h2>
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
              }}
            >
              <Field id="no-m" label="Forma de envio">
                <select
                  id="no-m"
                  className="input"
                  value={method}
                  onChange={(e) =>
                    setMethod(e.target.value === '' ? '' : ShippingMethod.parse(e.target.value))
                  }
                >
                  <option value="">Escolher</option>
                  {ShippingMethod.options.map((m) => (
                    <option key={m} value={m}>
                      {SHIPPING_METHOD_LABELS[m]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                id="no-sb"
                label="Prazo de envio"
                error={tried && shipBy.trim() && !shipIso ? 'Use DD/MM/AAAA.' : null}
              >
                <input
                  id="no-sb"
                  className="input"
                  inputMode="numeric"
                  value={shipBy}
                  onChange={(e) => setShipBy(e.target.value)}
                />
              </Field>
              <Field id="no-nf" label="Nota fiscal">
                <select
                  id="no-nf"
                  className="input"
                  value={nf}
                  onChange={(e) => setNf(NfStatus.parse(e.target.value))}
                >
                  {NfStatus.options.map((s) => (
                    <option key={s} value={s}>
                      {NF_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field id="no-kit" label="Kit de embalagem">
                <Combobox
                  id="no-kit"
                  value={kitId ?? channel?.defaultKitId ?? ''}
                  options={[
                    { value: '', label: 'Sem kit', pinned: 'top' },
                    ...(kits.data?.data ?? []).map(kitOption),
                  ]}
                  onChange={setKitId}
                />
              </Field>
            </div>
            {posted ? (
              <Field id="no-tr" label="Código de rastreio">
                <input
                  id="no-tr"
                  className="input"
                  placeholder="preencha quando postar"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                />
              </Field>
            ) : method === 'pickup' ? (
              <p className="sm muted">Cliente retira no ateliê: sem etiqueta nem rastreio.</p>
            ) : method === 'hand' ? (
              <p className="sm muted">Você entrega pessoalmente: sem etiqueta nem rastreio.</p>
            ) : null}
          </section>
        </div>

        <aside
          className="card col"
          style={{ flex: '1 1 280px', gap: 10, position: 'sticky', top: 16 }}
        >
          <h2>Resumo</h2>
          {p ? (
            <>
              <div className="kv">
                <span>Total da venda</span>
                <b className="v">{formatBRL(p.totalCentavos)}</b>
              </div>
              <div className="kv sm">
                <span className="muted">Comissão + tarifas</span>
                <span className="v">
                  −{' '}
                  {formatBRL(
                    p.predicted.commissionCentavos +
                      p.predicted.fixedFeeCentavos +
                      p.predicted.shippingCentavos +
                      p.predicted.taxCentavos +
                      p.predicted.discountsCentavos,
                  )}
                </span>
              </div>
              <div className="kv sm">
                <span className="muted">Custo (congelado agora)</span>
                <span className="v">− {formatBRL(p.estimatedCostCentavos)}</span>
              </div>
              <div className="kv">
                <span>Lucro estimado</span>
                <b
                  className="v"
                  style={{
                    color:
                      p.estimatedProfitCentavos < 0
                        ? 'var(--err)'
                        : p.estimatedMarginBps < 1500
                          ? 'var(--warn)'
                          : 'var(--ok)',
                  }}
                >
                  {formatBRL(p.estimatedProfitCentavos)}
                </b>
              </div>
              <p className="xs muted">
                {(p.estimatedMarginBps / 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
                % de margem. O lucro real é recalculado quando as impressões terminarem e o pedido
                fechar.
              </p>
              <span className="lbl">Ao confirmar</span>
              {p.effects.map((e) => (
                <p key={e} className="sm">
                  {e}
                </p>
              ))}
            </>
          ) : (
            <p className="sm muted">
              {preview.isError
                ? errorMessage(preview.error)
                : 'Adicione itens para ver o que acontece.'}
            </p>
          )}
          {tried && !valid ? (
            <Alert tone="err">
              <b>Falta pouco.</b>{' '}
              {lines.length === 0
                ? 'Adicione pelo menos um item.'
                : 'Confira as datas e as quantidades.'}
            </Alert>
          ) : null}
          {create.isError ? <Alert tone="err">{errorMessage(create.error)}</Alert> : null}
          <button
            type="button"
            className="btn p block"
            disabled={create.isPending}
            onClick={() => {
              setTried(true);
              if (valid && !(newName !== null && !newName.trim())) create.mutate();
            }}
          >
            Confirmar pedido
          </button>
        </aside>
      </div>
      {picking ? (
        <QuotePicker
          onClose={() => setPicking(false)}
          onPick={(q) => {
            setPicking(false);
            setQuoteId(q.id);
            if (q.channel) setChannelId(q.channel.id);
            if (q.customer) {
              setCustomerId(q.customer.id);
              setCustomerQ(q.customer.name);
            }
            setLines([
              ...lines,
              ...q.items.map((it) => ({
                key: crypto.randomUUID(),
                kind: 'quote' as const,
                variantId: '',
                quoteItemId: it.id,
                description: it.description,
                qty: String(it.quantity),
                price: centavosInput(it.unitPriceCentavos),
              })),
            ]);
            toast(`Itens de ${q.number} adicionados`);
          }}
        />
      ) : null}
    </>
  );
}
